import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Wifi, User, Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { setupAdmin } from '@/lib/api';

const Setup = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || form.username.length < 3) {
      toast.error('Kullanıcı adı en az 3 karakter olmalı');
      return;
    }
    if (form.password.length < 4) {
      toast.error('Şifre en az 4 karakter olmalı');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    setLoading(true);
    try {
      // Setup and auto-login
      const res = await setupAdmin(form.username.trim(), form.password);
      const { access_token, username: uname, role } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('username', uname);
      localStorage.setItem('role', role || 'admin');
      // Refresh auth context via login
      await login(form.username.trim(), form.password);
      toast.success('Yönetici hesabı oluşturuldu! Hoş geldiniz.');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kurulum başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 pointer-events-none" />

      <div className="w-full max-w-sm animate-fade-in-up">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary mx-auto flex items-center justify-center mb-4 shadow-lg shadow-primary/30">
            <Wifi className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Smart Office</h1>
          <p className="text-muted-foreground mt-1 text-sm">İlk Kurulum</p>
        </div>

        {/* Setup Banner */}
        <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 mb-5 flex items-start gap-3">
          <ShieldCheck size={16} className="text-primary mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">İlk yönetici hesabını oluşturun</p>
            <p className="text-muted-foreground text-xs mt-0.5">Bu hesap sistem yöneticisi (Admin) olarak atanacak.</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xl">
          <h2 className="text-lg font-semibold mb-5">Yönetici Hesabı Oluştur</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username">Kullanıcı Adı</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  placeholder="yönetici"
                  value={form.username}
                  onChange={e => set('username', e.target.value)}
                  className="pl-9"
                  autoComplete="username"
                  data-testid="setup-username-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  placeholder="En az 4 karakter"
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  className="pl-9 pr-10"
                  autoComplete="new-password"
                  data-testid="setup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm">Şifre Tekrar</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirm"
                  type="password"
                  placeholder="Şifreyi tekrar girin"
                  value={form.confirm}
                  onChange={e => set('confirm', e.target.value)}
                  className={`pl-9 ${form.confirm && form.password !== form.confirm ? 'border-destructive' : ''}`}
                  autoComplete="new-password"
                  data-testid="setup-confirm-input"
                />
              </div>
              {form.confirm && form.password !== form.confirm && (
                <p className="text-xs text-destructive">Şifreler eşleşmiyor</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full active-scale mt-2"
              disabled={loading}
              data-testid="setup-submit-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Oluşturuluyor...
                </div>
              ) : 'Kurulumu Tamamla'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Setup;
